import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit3, ShieldCheck, X, Save } from 'lucide-react';

// =====================================================================
// /admin/careers — Admin job board management.
// Requires admin auth (existing /api/auth/me with role=admin).
// =====================================================================

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  title: '',
  operator: '',
  base: '',
  role_type: 'permanent',
  reference: '',
  description: '',
  requirements: '',
  how_to_apply: '',
  verified: false,
  deadline: '',
};

const CareersAdminPage = () => {
  const [authed, setAuthed] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    document.title = 'Careers Admin — Gate 33';
    (async () => {
      try {
        const r = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        setAuthed(r.data && r.data.role === 'admin');
      } catch (e) {
        setAuthed(false);
      }
    })();
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/careers/admin/jobs`, { withCredentials: true });
      setJobs(r.data.items || []);
    } catch (e) {
      setMsg('Could not load jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) fetchJobs();
  }, [authed, fetchJobs]);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <span className="overline">Checking access…</span>
      </div>
    );
  }
  if (authed === false) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FDFBF7] px-6 text-center">
        <h1 className="font-heading text-3xl mb-3">Admin access required</h1>
        <p className="text-[#5A5A5A] mb-6">Please sign in with an admin account.</p>
        <a href="/login" className="btn-primary">Go to login</a>
      </div>
    );
  }

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setMsg('');
  };

  const openEdit = (job) => {
    setEditingId(job.id);
    setForm({
      title: job.title || '',
      operator: job.operator || '',
      base: job.base || '',
      role_type: job.role_type || 'permanent',
      reference: job.reference || '',
      description: job.description || '',
      requirements: job.requirements || '',
      how_to_apply: job.how_to_apply || '',
      verified: !!job.verified,
      deadline: job.deadline || '',
    });
    setShowForm(true);
    setMsg('');
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMsg('');
  };

  const save = async () => {
    setSaving(true); setMsg('');
    const payload = { ...form };
    if (!payload.deadline) payload.deadline = null;
    if (!payload.requirements) payload.requirements = null;
    if (!payload.how_to_apply) payload.how_to_apply = null;
    try {
      if (editingId) {
        await axios.patch(`${API}/api/careers/admin/jobs/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/careers/admin/jobs`, payload, { withCredentials: true });
      }
      await fetchJobs();
      closeForm();
    } catch (e) {
      setMsg('Save failed — please retry.');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this ad permanently?')) return;
    try {
      await axios.delete(`${API}/api/careers/admin/jobs/${id}`, { withCredentials: true });
      await fetchJobs();
    } catch (e) {
      setMsg('Delete failed.');
    }
  };

  const toggleArchive = async (job) => {
    try {
      await axios.patch(
        `${API}/api/careers/admin/jobs/${job.id}`,
        { archived: !job.archived },
        { withCredentials: true }
      );
      await fetchJobs();
    } catch (e) {
      setMsg('Update failed.');
    }
  };

  return (
    <div data-testid="careers-admin" className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="container-custom flex items-center justify-between py-4">
          <div>
            <div className="font-heading text-xl tracking-tight">
              Gate <span className="text-[#C5A880]">33</span> Careers · Admin
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A]">
              Manage the job board
            </div>
          </div>
          <button
            type="button"
            onClick={openCreate}
            data-testid="admin-new-job-cta"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={14} /> New ad
          </button>
        </div>
      </header>

      {msg && (
        <div className="container-custom mt-4">
          <div className="bg-white border border-[#C5A880] px-5 py-3 text-sm text-[#1A1A1A]">{msg}</div>
        </div>
      )}

      {/* List */}
      <section className="container-custom py-8">
        {loading ? (
          <div className="overline text-[#9A9A9A]">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="border border-dashed border-[#E5E0D8] p-10 text-center">
            <p className="text-[#5A5A5A]">No ads yet. Click <strong>New ad</strong> to post the first one.</p>
          </div>
        ) : (
          <ul className="space-y-4">
            {jobs.map((j) => (
              <li
                key={j.id}
                data-testid={`admin-job-${j.id}`}
                className={`bg-white border p-6 ${j.archived ? 'opacity-60 border-[#E5E0D8]' : 'border-[#E5E0D8] hover:border-[#C5A880]'} transition-colors`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-4 mb-3">
                  <div>
                    <h3 className="font-heading text-xl text-[#0F0F0F] leading-tight">
                      {j.title}
                      {j.verified && (
                        <span className="ml-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#C5A880]">
                          <ShieldCheck size={11} /> Verified
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-[#5A5A5A] mt-0.5">{j.operator}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(j)}
                      title="Edit"
                      className="text-[#5A5A5A] hover:text-[#1A1A1A] p-2"
                    >
                      <Edit3 size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleArchive(j)}
                      className="text-xs uppercase tracking-[0.15em] px-3 py-1 border border-[#E5E0D8] hover:border-[#1A1A1A]"
                    >
                      {j.archived ? 'Unarchive' : 'Archive'}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(j.id)}
                      title="Delete"
                      className="text-[#C0392B] hover:text-[#9A0F0F] p-2"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-[#5A5A5A] mb-3">
                  <span>{j.base}</span>
                  <span className="uppercase tracking-[0.16em] text-[#9A9A9A]">{j.role_type}</span>
                  {j.deadline && <span>Closes {j.deadline}</span>}
                  {j.posted_at && (
                    <span className="text-[#9A9A9A]">Posted {j.posted_at.slice(0, 10)}</span>
                  )}
                </div>
                <p className="text-sm text-[#1A1A1A] line-clamp-3 whitespace-pre-line">
                  {j.description}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* MODAL form */}
      {showForm && (
        <div
          data-testid="admin-job-form"
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        >
          <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-[#C5A880]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E0D8]">
              <h2 className="font-heading text-xl">
                {editingId ? 'Edit ad' : 'New job ad'}
              </h2>
              <button onClick={closeForm} className="p-1 text-[#5A5A5A] hover:text-[#1A1A1A]">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <Field label="Title*" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="form-title" />
              <Field label="Operator*" value={form.operator} onChange={(v) => setForm({ ...form, operator: v })} testId="form-operator" />
              <Field label="Reference code (optional, e.g. CC-AUT-2026)" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} testId="form-reference" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Base(s)*" value={form.base} onChange={(v) => setForm({ ...form, base: v })} testId="form-base" />
                <div>
                  <label className="overline mb-2 block">Role type*</label>
                  <select
                    value={form.role_type}
                    onChange={(e) => setForm({ ...form, role_type: e.target.value })}
                    data-testid="form-role-type"
                    className="w-full border border-[#E5E0D8] px-3 py-2 text-sm bg-white"
                  >
                    <option value="permanent">Permanent</option>
                    <option value="freelance">Freelance</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
              </div>

              <TextArea label="Description*" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={6} testId="form-description" />
              <TextArea label="Requirements (optional)" value={form.requirements} onChange={(v) => setForm({ ...form, requirements: v })} rows={4} testId="form-requirements" />
              <TextArea label="How to apply (optional)" value={form.how_to_apply} onChange={(v) => setForm({ ...form, how_to_apply: v })} rows={3} testId="form-howto" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="overline mb-2 block">Deadline (optional)</label>
                  <input
                    type="date"
                    value={form.deadline || ''}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                    data-testid="form-deadline"
                    className="w-full border border-[#E5E0D8] px-3 py-2 text-sm"
                  />
                </div>
                <label className="flex items-center gap-3 self-end pb-2">
                  <input
                    type="checkbox"
                    checked={form.verified}
                    onChange={(e) => setForm({ ...form, verified: e.target.checked })}
                    data-testid="form-verified"
                    className="w-4 h-4 accent-[#C5A880]"
                  />
                  <span className="text-sm text-[#1A1A1A]">Verified by Gate 33 badge</span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-[#E5E0D8] bg-[#FDFBF7] flex items-center justify-end gap-3">
              <button onClick={closeForm} className="text-xs uppercase tracking-[0.15em] px-5 py-3 border border-[#E5E0D8] hover:border-[#1A1A1A] transition-colors">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || !form.title || !form.operator || !form.base || !form.description}
                data-testid="form-save"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Save size={14} /> {saving ? 'Saving…' : 'Save ad'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, testId }) => (
  <div>
    <label className="overline mb-2 block">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full border border-[#E5E0D8] px-3 py-2 text-sm focus:border-[#C5A880] outline-none"
    />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 4, testId }) => (
  <div>
    <label className="overline mb-2 block">{label}</label>
    <textarea
      rows={rows}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full border border-[#E5E0D8] px-3 py-2 text-sm focus:border-[#C5A880] outline-none resize-y"
    />
  </div>
);

export default CareersAdminPage;
